import type { Analysis } from '@ni/shared';
import {
  analysisCaveats,
  analysisOfKind,
  analysisProse,
  relationshipLists,
} from '../../lib/analysis-copy';
import {
  RELATIONSHIP_HEAD,
  RELATIONSHIP_LIST_LABELS,
  RELATIONSHIP_SUB,
  type StatusForAnnotation,
} from '../../lib/copy';
import { Caveats } from '../ui/caveats';
import { Prose } from '../ui/prose';
import { KeptFromEarlier } from './kept-from-earlier';
import { LabelledList } from './labelled-list';
import { Section } from '../ui/section';

/**
 * **Relationship** — this person in relation to the organisation
 * (`relationship_read`, verticals note §0.8; the business vertical asks for it
 * at every status).
 *
 * It sits in the reading column beside Fit and is built on the same rule as Fit:
 * **no analysis, no block.** There is no empty state, no "not run yet", no
 * placeholder — a club dossier simply does not have this section, in the same
 * way a business dossier does not have Fit, and neither page says anything about
 * the other's absence. That is the whole of what makes a second vertical cost a
 * component rather than a conditional on every page (design note §0.3).
 *
 * Nothing here is gated on `org.vertical`. The presence of the analysis is the
 * gate, which stays right if a third vertical asks for this reading, and stays
 * right for a subject whose org changed its mind — the record shows the reading
 * that was written, not the one the vertical would ask for today.
 *
 * Labelled as interpretation where it sits among facts (ui-design §3.9), and it
 * closes with its own caveats rather than a shared bin: the hedging is about
 * this reading.
 */
export function RelationshipBlock({
  analyses,
  subject,
}: {
  analyses: Analysis[];
  /** The status block behind the kept-from-earlier line; absent, no line. */
  subject?: StatusForAnnotation;
}) {
  const relationship = analysisOfKind(analyses, 'relationship_read');
  if (!relationship) return null;

  const lists = relationshipLists(relationship);
  const hasLists = lists.connections.length > 0 || lists.angles.length > 0;
  const prose = analysisProse(relationship);

  return (
    <Section lead title={RELATIONSHIP_HEAD}>
      <div className="sub">{RELATIONSHIP_SUB}</div>
      <KeptFromEarlier subject={subject} kind="relationship_read" />

      {prose ? <Prose text={prose} className="notes" /> : null}

      {hasLists ? (
        <div className="talk-lists">
          <LabelledList label={RELATIONSHIP_LIST_LABELS.connections} lines={lists.connections} />
          <LabelledList label={RELATIONSHIP_LIST_LABELS.angles} lines={lists.angles} />
        </div>
      ) : null}

      <Caveats caveats={analysisCaveats(relationship)} />
    </Section>
  );
}
